// Load crypto polyfill first - before any other imports that might use it
import './polyfills/crypto.js';

// Import CSS files
import './index.css';
import './styles/a11y.css';

const DEBUG_CLIENT_LOGS = process.env.REACT_APP_DEBUG_CLIENT_LOGS === 'true';

// Polyfills for IE11 and older browsers removed - all natively supported in modern browsers

// Performance monitoring
const startTime = performance.now();

// Get the initial state from the server
const initialState = window.__INITIAL_STATE__ || {};
delete window.__INITIAL_STATE__;

// Initialize process if not defined
if (typeof window.process === 'undefined') {
  window.process = { env: {} };
}

// Set default environment variables if not set
if (!window.process.env.NODE_ENV) {
  window.process.env.NODE_ENV = 'development';
}

// Check if the app is running in an iframe (for development purposes)
const isInIframe = window.self !== window.top;

// Optimized app initialization with performance tracking
const initializeApp = async () => {
  const container = document.getElementById('root');
  if (!container) {
    throw new Error('Root element not found');
  }

  // Dynamic import of React, ReactDOM, and App core
  const [{ default: React }, { hydrateRoot, createRoot }, { AppRoot }] = await Promise.all([
    import('react'),
    import('react-dom/client'),
    import(/* webpackChunkName: "app-core" */ './App.js')
  ]);

  // Defer non-critical browser compatibility checks off the initial render path
  if (typeof window !== 'undefined') {
    const runCompatibility = () => {
      import('./utils/browserCompatibility.js')
        .then((m) => m.default?.init?.())
        .catch(() => {});
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(runCompatibility, { timeout: 12000 });
    } else {
      setTimeout(runCompatibility, 10000);
    }
  }

  // Create a render or hydrate function based on SSR state
  const renderApp = () => {
    const app = process.env.NODE_ENV === 'production'
      ? React.createElement(AppRoot)
      : React.createElement(React.StrictMode || (({ children }) => children), null, React.createElement(AppRoot));

    let root;

    // Check if there's server-rendered content for SEO
    const hasServerRenderedContent = container.innerHTML.trim().length > 0;

    // HYDRATE the build-time prerendered homepage when it is safe to do so:
    // the captured markup was produced by this same build with the default
    // theme and language, so only adopt it on the homepage routes for
    // English. Everything else keeps client rendering (no mismatch risk).
    const canHydrate =
      window.__PRERENDERED__ === true &&
      (window.location.pathname === '/' || window.location.pathname === '/home') &&
      String(localStorage.getItem('i18nextLng') || navigator.language || 'en').toLowerCase().startsWith('en');

    // In development mode, always use clean client-side createRoot to prevent dev hydration overlays
    if (process.env.NODE_ENV === 'development') {
      container.innerHTML = '';
      root = createRoot(container);
      root.render(app);
    } else if (canHydrate) {
      try {
        React.startTransition(() => {
          root = hydrateRoot(container, app, {
            onRecoverableError: (error) => {
              window.__lastRecoverableError = error?.message || String(error);
              console.error('Hydration recoverable error:', error?.message || error);
            }
          });
        });
      } catch (hydrationError) {
        window.__hydrationCatchError = hydrationError?.message || String(hydrationError);
        console.error('Hydration failed, falling back to client rendering:', hydrationError?.message || hydrationError);
        container.innerHTML = '';
        root = createRoot(container);
        root.render(app);
      }
    } else if (hasServerRenderedContent) {
      // Try to hydrate the server-rendered markup with error handling
      try {
        React.startTransition(() => {
          root = hydrateRoot(container, app, {
            onRecoverableError: (error) => {
              console.warn('Hydration recoverable error:', error.message);
            }
          });
        });
      } catch (hydrationError) {
        console.warn('Hydration failed, falling back to client rendering:', hydrationError.message);
        container.innerHTML = '';
        root = createRoot(container);
        root.render(app);
      }
    } else {
      // Create a new root for client-side rendering
      root = createRoot(container);
      root.render(app);
    }

    // In development, filter known non-blocking React framework warnings
    if (process.env.NODE_ENV === 'development') {
      const originalConsoleError = console.error;
      console.error = (...args) => {
        const message = typeof args[0] === 'string' ? args[0] : '';
        if (message.includes('Warning: Did not expect server HTML')) {
          return;
        }
        if (message.includes('Each child in a list should have a unique "key" prop')) {
          return;
        }
        originalConsoleError(...args);
      };
    }

    return root;
  };

  // Performance metrics
  const initTime = performance.now() - startTime;
  if (DEBUG_CLIENT_LOGS) {
    console.warn(`App initialized in ${initTime.toFixed(2)}ms`);
  }

  // This app is client-rendered in dev/prod; SSR state is not provided here.
  // Rendering immediately avoids noisy `loadableReady()` warnings.
  return renderApp();
};

// Schedule app initialization to yield to the main thread after module script evaluation
const scheduleAppInit = () => {
  initializeApp()
    .then((root) => {
      // Initialize analytics and reporting (never log metric objects in production)
      if (process.env.NODE_ENV === 'production' && process.env.DEBUG_CLIENT_LOGS === 'true') {
        import('./reportWebVitals.js').then((m) => m.default(console.log)).catch(() => {});
      }

      // Log performance metrics
      const totalTime = performance.now() - startTime;
      if (DEBUG_CLIENT_LOGS) {
        console.warn(`Total app load time: ${totalTime.toFixed(2)}ms`);
      }

      // Remove the pre-paint shell (sfc-shell) so the React app is visible.
      const removeShell = () => {
        const shell = document.getElementById('sfc-shell');
        if (shell && shell.parentNode) {
          shell.parentNode.removeChild(shell);
        }
      };
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(removeShell);
      } else {
        removeShell();
      }

      return root;
    })
  .catch((error) => {
    console.error('Failed to initialize app:', error);

    // Secure fallback: render error page without innerHTML XSS vulnerability
    const container = document.getElementById('root');
    if (container) {
      // Clear container content safely
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }

      // Create error elements safely using DOM API
      const errorDiv = document.createElement('div');
      errorDiv.style.cssText = 'text-align: center; padding: 50px; font-family: Arial, sans-serif; color: #333;';

      const titleElement = document.createElement('h1');
      titleElement.textContent = 'Application Error';
      titleElement.style.cssText = 'color: #dc3545; margin-bottom: 20px;';

      const messageElement = document.createElement('p');
      messageElement.textContent = 'Sorry, something went wrong while loading the application.';
      messageElement.style.cssText = 'margin-bottom: 10px; font-size: 16px;';

      const refreshElement = document.createElement('p');
      refreshElement.textContent = 'Please refresh the page to try again.';
      refreshElement.style.cssText = 'margin-bottom: 20px; font-size: 16px;';

      const refreshButton = document.createElement('button');
      refreshButton.textContent = 'Refresh Page';
      refreshButton.style.cssText = `
        background-color: #007bff;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
        transition: background-color 0.3s;
      `;
      refreshButton.addEventListener('click', () => {
        window.location.reload();
      });
      refreshButton.addEventListener('mouseover', () => {
        refreshButton.style.backgroundColor = '#0056b3';
      });
      refreshButton.addEventListener('mouseout', () => {
        refreshButton.style.backgroundColor = '#007bff';
      });

      // Assemble the error page
      errorDiv.appendChild(titleElement);
      errorDiv.appendChild(messageElement);
      errorDiv.appendChild(refreshElement);
      errorDiv.appendChild(refreshButton);

      // Add to container
      container.appendChild(errorDiv);

      // Log error details for debugging (without exposing sensitive info)
      if (process.env.NODE_ENV === 'development') {
        const errorDetails = document.createElement('pre');
        errorDetails.textContent = error.stack || error.message;
        errorDetails.style.cssText = `
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
          padding: 15px;
          margin-top: 20px;
          border-radius: 5px;
          text-align: left;
          font-size: 12px;
          overflow: auto;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        `;
        errorDiv.appendChild(errorDetails);
      }
    }
  });
};

let appInitialized = false;
const triggerAppInit = () => {
  if (appInitialized) return;
  appInitialized = true;
  scheduleAppInit();
};

if (typeof window !== 'undefined') {
  if (window.__PRERENDERING__) {
    // Prerender build step (Puppeteer): run immediately so capture is instant
    scheduleAppInit();
  } else {
    // Progressive Idle & Interaction Hydration:
    // Prerendered HTML provides 100% complete, genuine DOM immediately.
    // Hydrate immediately on any user intent, or yield to idle/background.
    const userEvents = ['pointerdown', 'touchstart', 'keydown', 'click', 'scroll'];
    const onUserInteraction = () => {
      userEvents.forEach((evt) => window.removeEventListener(evt, onUserInteraction));
      triggerAppInit();
    };
    userEvents.forEach((evt) => {
      window.addEventListener(evt, onUserInteraction, { passive: true, once: true });
    });

    const scheduleIdleInit = () => {
      const run = () => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(() => triggerAppInit(), { timeout: 10000 });
        } else if ('scheduler' in window && typeof window.scheduler.postTask === 'function') {
          window.scheduler.postTask(triggerAppInit, { priority: 'background' });
        } else {
          setTimeout(triggerAppInit, 8000);
        }
      };

      if (document.readyState === 'complete') {
        setTimeout(run, 6000);
      } else {
        window.addEventListener('load', () => {
          setTimeout(run, 6000);
        }, { once: true });
      }
    };
  }
} else {
  scheduleAppInit();
}

// PWA disabled - Service Worker registration removed per user request
